/**
 * Icon Registry Service
 * Centralized icon management with lazy loading support, WebP optimization, and localStorage persistence
 */

import { nowTimestamp } from './date';

const ICON_CACHE_KEY = 'seawatch_icon_cache';
const CACHE_VERSION = '1.1'; // Increment to invalidate old cache
const CACHE_EXPIRATION_DAYS = 30; // Cache expires after 30 days

interface IconCacheData {
  version: string;
  timestamp: number;
  icons: Record<string, string>;
}

type IconModule = { default: string };

/**
 * Try to load WebP, fallback to PNG
 * More robust error handling for missing files
 */
async function loadImageWithFallback(
  webpLoader: () => Promise<IconModule>,
  pngLoader: () => Promise<IconModule>,
): Promise<string> {
  // Try WebP first, but don't wait long if it fails
  try {
    const webpTimeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('WebP load timeout')), 2000),
    );
    const webpModule = await Promise.race([webpLoader(), webpTimeout]);

    // Validate the result
    if (webpModule?.default) {
      return webpModule.default;
    }
    throw new Error('WebP module invalid');
  } catch (webpError) {
    // WebP failed, try PNG fallback
    try {
      const pngModule = await pngLoader();
      if (pngModule?.default) {
        return pngModule.default;
      }
      throw new Error('PNG module invalid');
    } catch (pngError) {
      // Both failed - throw error with details
      throw new Error(
        `Failed to load icon: WebP error: ${webpError instanceof Error ? webpError.message : webpError}, PNG error: ${pngError instanceof Error ? pngError.message : pngError}`,
      );
    }
  }
}

// Icon path mappings - using dynamic imports for lazy loading
// Each icon has both WebP and PNG loaders for fallback support
const iconMap = {
  // Map icons
  'location-black': {
    webp: () => import('@/assets/icons/location-black.webp').catch(() => import('@/assets/icons/location-black.png')),
    png: () => import('@/assets/icons/location-black.png'),
  },
  'globe3-black': {
    webp: () => import('@/assets/icons/globe3-black.webp').catch(() => import('@/assets/icons/globe3-black.png')),
    png: () => import('@/assets/icons/globe3-black.png'),
  },
  globe: {
    webp: () => import('@/assets/icons/globe.webp').catch(() => import('@/assets/icons/globe.png')),
    png: () => import('@/assets/icons/globe.png'),
  },
  'ruler-icon': {
    webp: () => import('@/assets/icons/ruler-icon.webp').catch(() => import('@/assets/icons/ruler-icon.png')),
    png: () => import('@/assets/icons/ruler-icon.png'),
  },

  // Sidebar icons
  account: {
    webp: () => import('@/assets/icons/account.webp').catch(() => import('@/assets/icons/account.png')),
    png: () => import('@/assets/icons/account.png'),
  },
  report: {
    webp: () => import('@/assets/icons/report.webp').catch(() => import('@/assets/icons/report.png')),
    png: () => import('@/assets/icons/report.png'),
  },
  'aoi-icon-white': {
    webp: () => import('@/assets/icons/aoi-icon-white.webp').catch(() => import('@/assets/icons/aoi-icon-white.png')),
    png: () => import('@/assets/icons/aoi-icon-white.png'),
  },
  survaillance: {
    webp: () => import('@/assets/icons/survaillance.webp').catch(() => import('@/assets/icons/survaillance.png')),
    png: () => import('@/assets/icons/survaillance.png'),
  },
  'shipping-modified': {
    webp: () =>
      import('@/assets/icons/shipping-modified.webp').catch(() => import('@/assets/icons/shipping-modified.png')),
    png: () => import('@/assets/icons/shipping-modified.png'),
  },
  fleets: {
    webp: () => import('@/assets/icons/fleets.webp').catch(() => import('@/assets/icons/fleets.png')),
    png: () => import('@/assets/icons/fleets.png'),
  },
  'fusion-modified': {
    webp: () => import('@/assets/icons/fusion-modified.webp').catch(() => import('@/assets/icons/fusion-modified.png')),
    png: () => import('@/assets/icons/fusion-modified.png'),
  },
  'incidents-sidebar': {
    webp: () =>
      import('@/assets/icons/incidents-sidebar.webp').catch(() => import('@/assets/icons/incidents-sidebar.png')),
    png: () => import('@/assets/icons/incidents-sidebar.png'),
  },
  satellite: {
    webp: () => import('@/assets/icons/satellite.webp').catch(() => import('@/assets/icons/satellite.png')),
    png: () => import('@/assets/icons/satellite.png'),
  },
  signout: {
    webp: () => import('@/assets/icons/signout.webp').catch(() => import('@/assets/icons/signout.png')),
    png: () => import('@/assets/icons/signout.png'),
  },
  rules: {
    webp: () => import('@/assets/icons/rules.webp').catch(() => import('@/assets/icons/rules.png')),
    png: () => import('@/assets/icons/rules.png'),
  },
  tasking: {
    webp: () => import('@/assets/icons/tasking.webp').catch(() => import('@/assets/icons/tasking.png')),
    png: () => import('@/assets/icons/tasking.png'),
  },
  sanctions: {
    webp: () => import('@/assets/icons/sanctions.webp').catch(() => import('@/assets/icons/sanctions.png')),
    png: () => import('@/assets/icons/sanctions.png'),
  },
  gnss: {
    webp: () => import('@/assets/icons/gnss.webp').catch(() => import('@/assets/icons/gnss.png')),
    png: () => import('@/assets/icons/gnss.png'),
  },
  drone: {
    webp: () => import('@/assets/icons/drone.webp').catch(() => import('@/assets/icons/drone.png')),
    png: () => import('@/assets/icons/drone.png'),
  },
  turks: {
    webp: () => import('@/assets/icons/turks.webp').catch(() => import('@/assets/icons/turks.png')),
    png: () => import('@/assets/icons/turks.png'),
  },

  // MapLayers icons
  dropdown: {
    webp: () => import('@/assets/icons/dropdown.webp').catch(() => import('@/assets/icons/dropdown.png')),
    png: () => import('@/assets/icons/dropdown.png'),
  },
  triangle: {
    webp: () => import('@/assets/icons/triangle.webp').catch(() => import('@/assets/icons/triangle.png')),
    png: () => import('@/assets/icons/triangle.png'),
  },
  anchor3: {
    webp: () => import('@/assets/icons/anchor3.webp').catch(() => import('@/assets/icons/anchor3.png')),
    png: () => import('@/assets/icons/anchor3.png'),
  },
  jammer: {
    webp: () => import('@/assets/icons/jammer.webp').catch(() => import('@/assets/icons/jammer.png')),
    png: () => import('@/assets/icons/jammer.png'),
  },
  incidents: {
    webp: () => import('@/assets/icons/incidents.webp').catch(() => import('@/assets/icons/incidents.png')),
    png: () => import('@/assets/icons/incidents.png'),
  },
  eez: {
    webp: () => import('@/assets/icons/eez.webp').catch(() => import('@/assets/icons/eez.png')),
    png: () => import('@/assets/icons/eez.png'),
  },
  'weather-icon': {
    webp: () => import('@/assets/icons/weather-icon.webp').catch(() => import('@/assets/icons/weather-icon.png')),
    png: () => import('@/assets/icons/weather-icon.png'),
  },
  'weather-pressure': {
    webp: () =>
      import('@/assets/icons/weather-pressure.webp').catch(() => import('@/assets/icons/weather-pressure.png')),
    png: () => import('@/assets/icons/weather-pressure.png'),
  },
  'weather-cloud': {
    webp: () => import('@/assets/icons/weather-cloud.webp').catch(() => import('@/assets/icons/weather-cloud.png')),
    png: () => import('@/assets/icons/weather-cloud.png'),
  },
  'weather-wind': {
    webp: () => import('@/assets/icons/weather-wind.webp').catch(() => import('@/assets/icons/weather-wind.png')),
    png: () => import('@/assets/icons/weather-wind.png'),
  },
  'weather-temp': {
    webp: () => import('@/assets/icons/weather-temp.webp').catch(() => import('@/assets/icons/weather-temp.png')),
    png: () => import('@/assets/icons/weather-temp.png'),
  },

  // Vessel icons
  'vessels/cargo': {
    webp: () => import('@/assets/icons/vessels/cargo.webp').catch(() => import('@/assets/icons/vessels/cargo.png')),
    png: () => import('@/assets/icons/vessels/cargo.png'),
  },
  'vessels/tanker': {
    webp: () => import('@/assets/icons/vessels/tanker.webp').catch(() => import('@/assets/icons/vessels/tanker.png')),
    png: () => import('@/assets/icons/vessels/tanker.png'),
  },
  'vessels/passenger': {
    webp: () =>
      import('@/assets/icons/vessels/passenger.webp').catch(() => import('@/assets/icons/vessels/passenger.png')),
    png: () => import('@/assets/icons/vessels/passenger.png'),
  },
  'vessels/other_type': {
    webp: () =>
      import('@/assets/icons/vessels/other_type.webp').catch(() => import('@/assets/icons/vessels/other_type.png')),
    png: () => import('@/assets/icons/vessels/other_type.png'),
  },
  'vessels/reserved_for_future_use': {
    webp: () =>
      import('@/assets/icons/vessels/reserved_for_future_use.webp').catch(
        () => import('@/assets/icons/vessels/reserved_for_future_use.png'),
      ),
    png: () => import('@/assets/icons/vessels/reserved_for_future_use.png'),
  },
  'vessels/wing_in_ground': {
    webp: () =>
      import('@/assets/icons/vessels/wing_in_ground.webp').catch(
        () => import('@/assets/icons/vessels/wing_in_ground.png'),
      ),
    png: () => import('@/assets/icons/vessels/wing_in_ground.png'),
  },
  'vessels/high_speed_craft': {
    webp: () =>
      import('@/assets/icons/vessels/high_speed_craft.webp').catch(
        () => import('@/assets/icons/vessels/high_speed_craft.png'),
      ),
    png: () => import('@/assets/icons/vessels/high_speed_craft.png'),
  },
  'vessels/unknown_type': {
    webp: () =>
      import('@/assets/icons/vessels/unknown_type.webp').catch(() => import('@/assets/icons/vessels/unknown_type.png')),
    png: () => import('@/assets/icons/vessels/unknown_type.png'),
  },

  // Military icons
  chinese_research: {
    webp: () =>
      import('@/assets/icons/chinese_research.webp').catch(() => import('@/assets/icons/chinese_research.png')),
    png: () => import('@/assets/icons/chinese_research.png'),
  },
  chinese_energy: {
    webp: () => import('@/assets/icons/chinese_energy.webp').catch(() => import('@/assets/icons/chinese_energy.png')),
    png: () => import('@/assets/icons/chinese_energy.png'),
  },
  chinese_communication: {
    webp: () =>
      import('@/assets/icons/chinese_communication.webp').catch(
        () => import('@/assets/icons/chinese_communication.png'),
      ),
    png: () => import('@/assets/icons/chinese_communication.png'),
  },
  chinese_logistics: {
    webp: () =>
      import('@/assets/icons/chinese_logistics.webp').catch(() => import('@/assets/icons/chinese_logistics.png')),
    png: () => import('@/assets/icons/chinese_logistics.png'),
  },
  chinese_nuclear: {
    webp: () => import('@/assets/icons/chinese_nuclear.webp').catch(() => import('@/assets/icons/chinese_nuclear.png')),
    png: () => import('@/assets/icons/chinese_nuclear.png'),
  },
  chinese_training: {
    webp: () =>
      import('@/assets/icons/chinese_training.webp').catch(() => import('@/assets/icons/chinese_training.png')),
    png: () => import('@/assets/icons/chinese_training.png'),
  },
  chinese_satellite: {
    webp: () =>
      import('@/assets/icons/chinese_satellite.webp').catch(() => import('@/assets/icons/chinese_satellite.png')),
    png: () => import('@/assets/icons/chinese_satellite.png'),
  },
  alert: {
    webp: () => import('@/assets/icons/alert.webp').catch(() => import('@/assets/icons/alert.png')),
    png: () => import('@/assets/icons/alert.png'),
  },

  // Power plant icons
  'power-hydro': {
    webp: () => import('@/assets/icons/power-hydro.webp').catch(() => import('@/assets/icons/power-hydro.png')),
    png: () => import('@/assets/icons/power-hydro.png'),
  },
  'power-solar': {
    webp: () => import('@/assets/icons/power-solar.webp').catch(() => import('@/assets/icons/power-solar.png')),
    png: () => import('@/assets/icons/power-solar.png'),
  },
  'power-gas': {
    webp: () => import('@/assets/icons/power-gas.webp').catch(() => import('@/assets/icons/power-gas.png')),
    png: () => import('@/assets/icons/power-gas.png'),
  },
  'power-other': {
    webp: () => import('@/assets/icons/power-other.webp').catch(() => import('@/assets/icons/power-other.png')),
    png: () => import('@/assets/icons/power-other.png'),
  },
  'power-oil': {
    webp: () => import('@/assets/icons/power-oil.webp').catch(() => import('@/assets/icons/power-oil.png')),
    png: () => import('@/assets/icons/power-oil.png'),
  },
  'power-wind': {
    webp: () => import('@/assets/icons/power-wind.webp').catch(() => import('@/assets/icons/power-wind.png')),
    png: () => import('@/assets/icons/power-wind.png'),
  },
  'power-nuclear': {
    webp: () => import('@/assets/icons/power-nuclear.webp').catch(() => import('@/assets/icons/power-nuclear.png')),
    png: () => import('@/assets/icons/power-nuclear.png'),
  },
  'power-coal': {
    webp: () => import('@/assets/icons/power-coal.webp').catch(() => import('@/assets/icons/power-coal.png')),
    png: () => import('@/assets/icons/power-coal.png'),
  },
  'power-waste': {
    webp: () => import('@/assets/icons/power-waste.webp').catch(() => import('@/assets/icons/power-waste.png')),
    png: () => import('@/assets/icons/power-waste.png'),
  },
  'power-biomass': {
    webp: () => import('@/assets/icons/power-biomass.webp').catch(() => import('@/assets/icons/power-biomass.png')),
    png: () => import('@/assets/icons/power-biomass.png'),
  },
  'power-wave-and-tidal': {
    webp: () =>
      import('@/assets/icons/power-wave-and-tidal.webp').catch(() => import('@/assets/icons/power-wave-and-tidal.png')),
    png: () => import('@/assets/icons/power-wave-and-tidal.png'),
  },
  'power-petcoke': {
    webp: () => import('@/assets/icons/power-petcoke.webp').catch(() => import('@/assets/icons/power-petcoke.png')),
    png: () => import('@/assets/icons/power-petcoke.png'),
  },
  'power-geothermal': {
    webp: () =>
      import('@/assets/icons/power-geothermal.webp').catch(() => import('@/assets/icons/power-geothermal.png')),
    png: () => import('@/assets/icons/power-geothermal.png'),
  },
  'power-storage': {
    webp: () => import('@/assets/icons/power-storage.webp').catch(() => import('@/assets/icons/power-storage.png')),
    png: () => import('@/assets/icons/power-storage.png'),
  },
  'power-cogeneration': {
    webp: () =>
      import('@/assets/icons/power-cogeneration.webp').catch(() => import('@/assets/icons/power-cogeneration.png')),
    png: () => import('@/assets/icons/power-cogeneration.png'),
  },

  // Panel icons
  'pin-enabled': {
    webp: () => import('@/assets/icons/pin-enabled.webp').catch(() => import('@/assets/icons/pin-enabled.png')),
    png: () => import('@/assets/icons/pin-enabled.png'),
  },
  'pin-disabled': {
    webp: () => import('@/assets/icons/pin-disabled.webp').catch(() => import('@/assets/icons/pin-disabled.png')),
    png: () => import('@/assets/icons/pin-disabled.png'),
  },

  // Feed icons
  'brain-icon': {
    webp: () => import('@/assets/icons/brain-icon.webp').catch(() => import('@/assets/icons/brain-icon.png')),
    png: () => import('@/assets/icons/brain-icon.png'),
  },

  // Navigation & UI icons
  back: {
    webp: () => import('@/assets/icons/back.webp').catch(() => import('@/assets/icons/back.png')),
    png: () => import('@/assets/icons/back.png'),
  },
  'image-icon': {
    webp: () => import('@/assets/icons/image-icon.webp').catch(() => import('@/assets/icons/image-icon.png')),
    png: () => import('@/assets/icons/image-icon.png'),
  },
  'close-white': {
    webp: () => import('@/assets/icons/close-white.webp').catch(() => import('@/assets/icons/close-white.png')),
    png: () => import('@/assets/icons/close-white.png'),
  },
  'send-icon': {
    webp: () => import('@/assets/icons/send-icon.webp').catch(() => import('@/assets/icons/send-icon.png')),
    png: () => import('@/assets/icons/send-icon.png'),
  },
  'trash-icon': {
    webp: () => import('@/assets/icons/trash-icon.webp').catch(() => import('@/assets/icons/trash-icon.png')),
    png: () => import('@/assets/icons/trash-icon.png'),
  },
  'star-outline': {
    webp: () => import('@/assets/icons/star-outline.webp').catch(() => import('@/assets/icons/star-outline.png')),
    png: () => import('@/assets/icons/star-outline.png'),
  },
  add: {
    webp: () => import('@/assets/icons/add.webp').catch(() => import('@/assets/icons/add.png')),
    png: () => import('@/assets/icons/add.png'),
  },
  vessels: {
    webp: () =>
      import('@/assets/icons/shipping-modified.webp').catch(() => import('@/assets/icons/shipping-modified.png')),
    png: () => import('@/assets/icons/shipping-modified.png'),
  },

  // Network icon (CDC Pipeline)
  'share-network': {
    webp: () => import('@/assets/icons/share-network.png'),
    png: () => import('@/assets/icons/share-network.png'),
  },

  // Illegal fishing sidebar icon
  'illegal-fishing': {
    webp: () => import('@/assets/icons/illegal-fishing.png'),
    png: () => import('@/assets/icons/illegal-fishing.png'),
  },

  // Add more icons as needed
};

export type IconName = keyof typeof iconMap;

// Cache for loaded icons (memory cache for fast access)
const iconCache: Record<string, string> = {};

// Debounce timer for saving to localStorage
let saveTimer: ReturnType<typeof setTimeout> | null = null;
const SAVE_DEBOUNCE_MS = 500; // Wait 500ms before saving to localStorage

/**
 * Clear icon cache from memory (internal helper)
 */
function clearMemoryCache(): void {
  Object.keys(iconCache).forEach((key) => {
    delete iconCache[key];
  });
}

/**
 * Clear icon cache from localStorage (internal helper)
 */
function clearStorageCache(): void {
  try {
    localStorage.removeItem(ICON_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing icon cache from localStorage:', error);
  }
}

/**
 * Load icon cache from localStorage
 * @returns True if cache was loaded successfully
 */
function loadIconCacheFromStorage(): boolean {
  try {
    const cached = localStorage.getItem(ICON_CACHE_KEY);
    if (!cached) return false;

    const cacheData: IconCacheData = JSON.parse(cached);

    // Check cache version
    if (cacheData.version !== CACHE_VERSION) {
      clearMemoryCache();
      clearStorageCache();
      return false;
    }

    // Check cache expiration
    const now = nowTimestamp();
    const expirationTime = CACHE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
    if (now - cacheData.timestamp > expirationTime) {
      clearMemoryCache();
      clearStorageCache();
      return false;
    }

    // Restore cache to memory, but filter out invalid entries
    const validIcons: Record<string, string> = {};
    for (const [name, url] of Object.entries(cacheData.icons)) {
      // Basic validation - URL should be a string and not empty
      if (url && typeof url === 'string' && url.length > 0) {
        // Check if URL looks valid (starts with http, /, or data:)
        if (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:') || url.includes('.')) {
          validIcons[name] = url;
        }
      }
    }

    Object.assign(iconCache, validIcons);

    // If we filtered out some icons, update the cache
    if (Object.keys(validIcons).length !== Object.keys(cacheData.icons).length) {
      saveIconCacheToStorage();
    }

    return true;
  } catch (error) {
    console.error('Error loading icon cache from localStorage:', error);
    return false;
  }
}

/**
 * Save icon cache to localStorage (internal implementation)
 */
function _saveIconCacheToStorage(): void {
  try {
    const cacheData: IconCacheData = {
      version: CACHE_VERSION,
      timestamp: nowTimestamp(),
      icons: { ...iconCache },
    };

    const cacheString = JSON.stringify(cacheData);
    const cacheSize = new Blob([cacheString]).size;

    // Check if cache is too large (localStorage limit is typically 5-10MB)
    // Warn if approaching limit (4MB threshold)
    if (cacheSize > 4 * 1024 * 1024) {
      console.warn('Icon cache is getting large:', (cacheSize / 1024 / 1024).toFixed(2), 'MB');
    }

    localStorage.setItem(ICON_CACHE_KEY, cacheString);
  } catch (error) {
    // Handle quota exceeded error
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old icon cache');
      clearIconCache();
    } else {
      console.error('Error saving icon cache to localStorage:', error);
    }
  }
}

/**
 * Save icon cache to localStorage with debouncing
 * This prevents excessive writes when multiple icons load quickly
 */
function saveIconCacheToStorage(): void {
  // Clear existing timer
  if (saveTimer) {
    clearTimeout(saveTimer);
  }

  // Set new timer
  saveTimer = setTimeout(() => {
    _saveIconCacheToStorage();
    saveTimer = null;
  }, SAVE_DEBOUNCE_MS);
}

/**
 * Clear icon cache from both memory and localStorage
 */
export function clearIconCache(): void {
  clearMemoryCache();
  clearStorageCache();
}

/**
 * Get icon cache size in bytes
 * @returns Size of cache in bytes
 */
export function getIconCacheSize(): number {
  try {
    const cached = localStorage.getItem(ICON_CACHE_KEY);
    if (!cached) return 0;
    return new Blob([cached]).size;
  } catch (error) {
    console.error('Error getting icon cache size:', error);
    return 0;
  }
}

// Initialize cache from localStorage on module load
loadIconCacheFromStorage();

/**
 * Validate if an icon URL is still valid
 * Checks if the URL points to a file that exists
 */
async function validateIconUrl(url: string): Promise<boolean> {
  if (!url) return false;

  try {
    // Try to fetch the image to verify it exists
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    // If fetch fails, try creating an image element
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
      // Timeout after 2 seconds
      setTimeout(() => resolve(false), 2000);
    });
  }
}

/**
 * Get icon URL - lazy loads on demand with WebP fallback to PNG
 * @param iconName - Name of the icon (key from iconMap)
 * @returns Promise that resolves to the icon URL
 */
export async function getIcon(iconName: IconName): Promise<string> {
  // Check if icon exists in map first
  const iconLoaders = iconMap[iconName];
  if (!iconLoaders) {
    console.warn(`Icon "${iconName}" not found in registry`);
    return '';
  }

  // Check cached icon
  if (iconCache[iconName]) {
    const cachedUrl = iconCache[iconName];
    if (cachedUrl && (cachedUrl.startsWith('http') || cachedUrl.startsWith('/') || cachedUrl.startsWith('data:'))) {
      return cachedUrl;
    }
    // Invalid cached URL, remove it
    delete iconCache[iconName];
  }

  try {
    // Try WebP first, fallback to PNG
    const iconUrl = await loadImageWithFallback(iconLoaders.webp, iconLoaders.png);

    if (!iconUrl) {
      console.warn(`Icon "${iconName}" loaded but URL is empty`);
      return '';
    }

    // Cache the result in memory
    iconCache[iconName] = iconUrl;

    // Save to localStorage (debounced)
    saveIconCacheToStorage();

    return iconUrl;
  } catch (error) {
    console.error(`Failed to load icon "${iconName}":`, error);
    // Remove from cache if it was there
    if (iconCache[iconName]) {
      delete iconCache[iconName];
      saveIconCacheToStorage();
    }
    return '';
  }
}

/**
 * Preload critical icons (for initial render)
 * @param iconNames - Array of icon names to preload
 */
export async function preloadIcons(iconNames: IconName[]): Promise<void> {
  await Promise.all(iconNames.map((name) => getIcon(name)));
}

/**
 * Get icon synchronously if already cached
 * @param iconName - Name of the icon
 * @returns Icon URL if cached, empty string otherwise
 */
export function getCachedIcon(iconName: IconName): string {
  return iconCache[iconName] || '';
}

/**
 * Check if icon is cached
 * @param iconName - Name of the icon
 * @returns True if cached
 */
export function isIconCached(iconName: IconName): boolean {
  return !!iconCache[iconName];
}
